import { Anchor, Text } from '@mantine/core';
import { Link } from 'react-router';
import { formatOrder, type PayinOrder } from '~/entities/payin';
import { getCurrencyIcon } from '~/entities/currency';
import { getOrderPath, type OrderOrigin, type OrderRouteState } from '~/shared/lib/orderRoutes';
import { type TableRow } from '~/shared/ui/Table';
import { OrderCountdown, OrderStatusBadge } from '../../../../OrderManagement';
import { Requisite } from '../../../../components';
import { OrderTableColumn } from './columns';

interface PrepareTableRowsProps {
    orders?: PayinOrder[];
    origin: OrderOrigin;
}

export const prepareTableRows = ({ orders, origin }: PrepareTableRowsProps): TableRow<PayinOrder>[] => {
    if (!orders) {
        return [];
    }

    return orders.map((order) => {
        const orderId = order.id?.value;
        const routeState = { from: origin } satisfies OrderRouteState;

        return {
            key: orderId ?? 0,
            cells: [
                {
                    key: OrderTableColumn.ID,
                    content: orderId ? (
                        <Anchor
                            component={Link}
                            state={routeState}
                            to={getOrderPath('payin', orderId)}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <Text
                                truncate='end'
                                maw={128}
                            >
                                {orderId}
                            </Text>
                        </Anchor>
                    ) : (
                        <Text
                            truncate='end'
                            maw={128}
                        />
                    ),
                },
                {
                    key: OrderTableColumn.CREATED_AT,
                    content: <Text>{formatOrder.date(order.createdAt)}</Text>,
                },
                {
                    key: OrderTableColumn.REQUISITE,
                    content: <Requisite requisite={order.requisite} />,
                },
                {
                    key: OrderTableColumn.AMOUNT,
                    content: <Text>{order.amount?.value}</Text>,
                },
                {
                    key: OrderTableColumn.CURRENCY,
                    content: <Text>{getCurrencyIcon(order.currency)}</Text>,
                },
                {
                    key: OrderTableColumn.RATE,
                    content: <Text>{order.rate?.value ?? '-'}</Text>,
                },
                {
                    key: OrderTableColumn.STATUS,
                    content: (
                        <OrderStatusBadge
                            status={order.publicStatus}
                            fullWidth
                        />
                    ),
                },
                {
                    key: OrderTableColumn.EXPIRES_AT,
                    content: <OrderCountdown expiresAt={order.expiresAt} />,
                },
            ],
            item: order,
        };
    });
};
